import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import Layout from "../../components/layout";
import {
  FaArrowLeft,
  FaFileInvoice,
  FaSearch,
  FaSpinner,
} from "react-icons/fa";
import { MdCheckBoxOutlineBlank } from "react-icons/md";
import { ImPrinter } from "react-icons/im";

import styles from "./OpenJobworkListPage.module.scss";
import { Link, useNavigate, useParams } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchOpenJobworksData,
  fetchPartiesData,
  getJobworks,
  getParties,
  getSelectedJobwork,
  getSelectedParty,
  getTotalJobworks,
  resetFilters,
  updateSelectedJobwork,
  updateSelectedParty,
} from "../../features/openJobwork/openJobworkSlice";
import ListCard from "../../components/jobworks/ListCard";
import {
  ICloseFormInput,
  OptionsProps,
  JobworkProps,
} from "../../types/jobwork";
import Select from "react-select";
import { selectStyles } from "../../static/styles";
import { FaSquareCheck } from "react-icons/fa6";
import { POST } from "../../services/axios.service";
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";

const OpenJobworkListPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  let debounceTimeout: NodeJS.Timeout;
  const { typeId } = useParams();
  const jobworks = useAppSelector(getJobworks);
  const totalJobworks = useAppSelector(getTotalJobworks);
  const parties = useAppSelector(getParties);
  const selectedParty = useAppSelector(getSelectedParty);
  const selectedJobwork = useAppSelector(getSelectedJobwork);

  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [firstLoading, setFirstLoading] = useState<boolean>(false);
  const [partiesOption, setPartiesOption] = useState<OptionsProps[]>([]);
  const [printJobworks, setPrintJobworks] = useState<JobworkProps[]>([]);
  const [invoiceJobworks, setInvoiceJobworks] = useState<JobworkProps[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const methods = useForm<ICloseFormInput>();

  const { handleSubmit, control, reset } = methods;

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "jobworks",
  });

  // Drops the jobworks picked for the invoice together with their delivered weight.
  // flushSync commits the empty list (unmounting the delivered-weight inputs)
  // before any other re-render, such as the sync one a Redux filter change
  // triggers, can re-register those inputs into the just-reset form and leave
  // `jobworks[]` entries without a jobwork_id (the next jobwork-close then
  // fails with 422). Call from event handlers / async callbacks only, not from
  // effects.
  const clearInvoiceJobworks = () => {
    flushSync(() => {
      setInvoiceJobworks([]);
      reset({ jobworks: [] });
    });
  };

  const onSubmit: SubmitHandler<ICloseFormInput> = async (data) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      await POST("/v1/worker/jobwork-close", data)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const jobworkIds = data.jobworks.map(
              (jobwork: any) => jobwork.jobwork_id
            );

            clearInvoiceJobworks();

            // Clear the filters so the next party starts from a clean list.
            // Resetting selectedParty/selectedJobwork re-runs the fetch effect.
            setShowFilter(false);
            dispatch(resetFilters());
            setPage(1);

            await POST(`/v1/worker/jobworks-reports`, {
              jobwork_ids: jobworkIds,
            })
              .then(async (response: any) => {
                if (response.data.status === "success") {
                  const downloadLink = response.data.data.download_link;
                  try {
                    const newWindow = window.open(downloadLink, "_blank");
                    if (
                      !newWindow ||
                      newWindow.closed ||
                      typeof newWindow.closed == "undefined"
                    ) {
                      throw new Error("પોપ્અપ બ્લોક કરેલ છે.");
                    }
                  } catch (e) {
                    window.location.href = downloadLink;
                  }
                } else {
                  setErrorMessage(response.data.message);
                }
              })
              .catch((error: any) => {
                setErrorMessage(error.data.message);
              });
          } else {
            setErrorMessage(response.data.message);
          }
        })
        .catch((error: any) => {
          setErrorMessage(error.data.message);
        });
    } catch (error) {
      setErrorMessage("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
    }

    setIsLoading(false);
  };

  const handleGoToBack = () => {
    navigate(route.dashboard);
  };

  const handleChangeParty = (id: number) => {
    setErrorMessage(null);
    // An invoice is for one party, so drop jobworks picked for the previous one.
    if (id !== selectedParty && invoiceJobworks.length > 0)
      clearInvoiceJobworks();
    dispatch(updateSelectedParty(id));
    setPage(1);
  };

  const handleChangeJobworkId = (id: number | null) => {
    dispatch(updateSelectedJobwork(id));
    setPage(1);
  };

  const handleChangePrintJobwork = (jobwork: JobworkProps, action: boolean) => {
    setPrintJobworks((prevJobworks) => {
      if (action) {
        if (
          !prevJobworks.some(
            (existingJobwork) => existingJobwork.id === jobwork.id
          )
        ) {
          return [...prevJobworks, jobwork];
        }
        return prevJobworks;
      } else {
        return prevJobworks.filter(
          (existingJobwork) => existingJobwork.id !== jobwork.id
        );
      }
    });
  };

  const handleChangeInvoiceJobwork = (
    jobwork: JobworkProps,
    action: boolean
  ) => {
    setErrorMessage(null);

    if (selectedParty <= 0) {
      setErrorMessage("કૃપા કરીને પાર્ટી નું નામ સિલેક્ટ કરો.");
      return;
    }

    // append/remove stay outside the state updater: StrictMode may run an
    // updater twice, which would add or remove the form entry twice.
    if (action) {
      if (
        invoiceJobworks.some(
          (existingJobwork) => existingJobwork.id === jobwork.id
        )
      )
        return;

      append({
        jobwork_id: jobwork.id,
        i_delivered_weight: jobwork.i_delivered_weight,
      });
      setInvoiceJobworks((prevJobworks) => [...prevJobworks, jobwork]);
    } else {
      const index = fields.findIndex(
        (field) => field.jobwork_id === jobwork.id
      );
      if (index >= 0) remove(index);

      setInvoiceJobworks((prevJobworks) =>
        prevJobworks.filter(
          (existingJobwork) => existingJobwork.id !== jobwork.id
        )
      );
    }
  };

  const handlePrintReport = async () => {
    setErrorMessage(null);
    try {
      await POST(`/v1/worker/jobworks-reports`, {
        jobwork_ids: printJobworks.map((jobwork) => jobwork.id) || [],
      })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            setPrintJobworks([]);
            const downloadLink = response.data.data.download_link;
            try {
              const newWindow = window.open(downloadLink, "_blank");
              if (
                !newWindow ||
                newWindow.closed ||
                typeof newWindow.closed == "undefined"
              ) {
                throw new Error("પોપ્અપ બ્લોક કરેલ છે.");
              }
            } catch (e) {
              window.location.href = downloadLink;
            }
          } else {
            setErrorMessage(response.data.message);
          }
        })
        .catch((error: any) => {
          setErrorMessage(error.data.message);
        });
    } catch (error) {
      setErrorMessage("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
    }
  };

  useEffect(() => {
    if (page === 1) setFirstLoading(true);
    setIsLoading(true);

    dispatch(
      fetchOpenJobworksData({
        page: page,
        party_id: selectedParty ? selectedParty : undefined,
        jobwork_id: selectedJobwork ? selectedJobwork : undefined,
        type: typeId === "1" ? 0 : 1,
      })
    ).then(() => {
      setIsLoading(false);
      setFirstLoading(false);
    });
  }, [page, selectedParty, selectedJobwork]);

  useEffect(() => {
    dispatch(fetchPartiesData({ type: parseInt(typeId || "1") }));
  }, [typeId]);

  useEffect(() => {
    if (parties) {
      const options: OptionsProps[] = [];
      parties?.map((party) => {
        options.push({
          value: party.id,
          label: party.name + (party.phone ? " (" + party.phone + ")" : ""),
        });
      });
      setPartiesOption(options);
    }
  }, [parties]);

  return (
    <Layout>
      <div className={styles.OpenJobworkListPage}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
              <FaArrowLeft
                onClick={handleGoToBack}
                className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
              />

              <h1 className="text-4xl mt-6 font-bold text-white">
                {typeId === "1" ? "બાંધવાનું ચાલુ કામ" : "ખોલવાનું ચાલુ કામ"}
              </h1>

              <div className="absolute top-5 right-5 flex gap-3">
                {printJobworks.length > 0 && (
                  <ImPrinter
                    onClick={handlePrintReport}
                    className="text-3xl text-white"
                  />
                )}

                <button
                  className={`${
                    invoiceJobworks.length > 0 && !isLoading
                      ? "inline"
                      : "hidden"
                  }`}
                  disabled={invoiceJobworks.length === 0 || isLoading}
                  type="submit"
                >
                  <FaFileInvoice className="text-3xl text-white" />
                </button>

                {invoiceJobworks.length > 0 && isLoading && (
                  <div className="flex items-center justify-center">
                    <FaSpinner className="text-3xl text-white animate-spin" />
                  </div>
                )}
              </div>
            </div>

            <div className="w-full relative mt-[70px] px-10 py-5 lg:mt-[20px]">
              {errorMessage && (
                <p className="text-red-500 text-sm font-semibold text-center mb-5">
                  {errorMessage}
                </p>
              )}

              <div className="flex mb-5">
                <button
                  className="p-2 w-fit ml-auto rounded-md flex items-center bg-gradient-to-r from-primary-1 to-primary-2 text-white transition-colors duration-300"
                  onClick={() => setShowFilter(!showFilter)}
                  type="button"
                >
                  <FaSearch className="mr-2" />{" "}
                  {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
                </button>
              </div>

              {showFilter && (
                <>
                  <div className="bg-gradient-to-r mb-5 from-primary-1 to-primary-2 h-16 w-full relative rounded">
                    <Select
                      isClearable
                      value={
                        partiesOption.find(
                          (val) => val.value === selectedParty
                        ) ?? null
                      }
                      className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      styles={selectStyles}
                      placeholder={"પાર્ટી નું નામ દાખલ કરો"}
                      options={partiesOption}
                      onChange={(val: any) =>
                        handleChangeParty(val?.value ?? 0)
                      }
                    />
                  </div>
                  <div className="bg-gradient-to-r mb-10 from-primary-1 to-primary-2 h-16 w-full relative rounded">
                    <input
                      defaultValue={selectedJobwork || ""}
                      className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      placeholder="કામ નું આઇડી દાખલ કરો"
                      pattern="[0-9]+(\.[0-9]+)?"
                      type="number"
                      onKeyUp={(e: any) => {
                        const value = e.target.value;

                        clearTimeout(debounceTimeout);
                        debounceTimeout = setTimeout(() => {
                          handleChangeJobworkId(value || null);
                        }, 2000);
                      }}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                </>
              )}

              {firstLoading ? (
                <p className="mt-4 text-primary-2 font-bold text-2xl text-center">
                  રાહ જુઓ...
                </p>
              ) : (
                <>
                  {jobworks && jobworks.length > 0 ? (
                    <div>
                      {jobworks.map((jobwork, index) => (
                        <div className="relative w-full h-full" key={index}>
                          <ListCard
                            jobwork={jobwork}
                            index={fields.findIndex(
                              (o) => o.jobwork_id === jobwork.id
                            )}
                          />

                          <div className="absolute top-3 right-3 flex gap-3">
                            {printJobworks.some((o) => o.id === jobwork.id) ? (
                              <div>
                                <ImPrinter className="text-xl text-primary-1" />
                                <FaSquareCheck
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    handleChangePrintJobwork(jobwork, false);
                                  }}
                                  className="text-xl text-primary-1"
                                />
                              </div>
                            ) : (
                              <div>
                                <ImPrinter className="text-xl text-primary-1" />
                                <MdCheckBoxOutlineBlank
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    handleChangePrintJobwork(jobwork, true);
                                  }}
                                  className="text-xl text-primary-1"
                                />
                              </div>
                            )}

                            {invoiceJobworks.some(
                              (o) => o.id === jobwork.id
                            ) ? (
                              <div>
                                <FaFileInvoice className="text-xl text-primary-1" />
                                <FaSquareCheck
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    handleChangeInvoiceJobwork(jobwork, false);
                                  }}
                                  className="text-xl text-primary-1"
                                />
                              </div>
                            ) : (
                              <div>
                                <FaFileInvoice className="text-xl text-primary-1" />
                                <MdCheckBoxOutlineBlank
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    handleChangeInvoiceJobwork(jobwork, true);
                                  }}
                                  className="text-xl text-primary-1"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="mt-4 text-primary-2 font-bold text-2xl">
                        હાલ કોઈ કામ ની વિગતો મળી નથી.
                      </p>

                      <div className="w-full flex justify-center">
                        <Link
                          to={route.newJobwork.replace(
                            ":typeId",
                            typeId || "1"
                          )}
                        >
                          <button
                            type="button"
                            className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                          >
                            કામ ઉમેરો
                          </button>
                        </Link>
                      </div>
                    </div>
                  )}

                  {totalJobworks > 0 && totalJobworks > jobworks.length && (
                    <div className="flex">
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={isLoading}
                        type="button"
                        className="mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                      >
                        {isLoading ? "રાહ જુઓ..." : "વધુ જુઓ"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </Layout>
  );
};
export default OpenJobworkListPage;
